/**
 * WS region picker (multi-node backend, added 2026-07-23).
 *
 * The backend runs identical Phoenix nodes in multiple regions (EU Nuremberg,
 * US Manassas) clustered over WireGuard; `GET /api/v1/regions` lists them. On
 * startup we apply the cached choice synchronously (so the login-time socket
 * already uses it), then latency-probe every region in the background and
 * persist the fastest for this and future launches. A mid-session switch is
 * deliberately NOT forced — reconnect churn costs more than a few ms of RTT;
 * the better region simply wins on the next connect.
 *
 * The probe target is /regions itself: it is DB-free on every node, so it
 * measures the node's own proximity rather than its tunnel trip to Postgres.
 *
 * Two rules keep the STORED preference honest (2026-08-27, after a European
 * user spent multiple launches pinned to the US edge):
 *
 *   1. A failover is SESSION-SCOPED. `failoverRegion()` is a reaction to a
 *      dead socket, not a statement about latency — a backend deploy that
 *      restarts ws-de1 produces the four transport errors that trigger it. It
 *      moves the live socket only; the stored preference is untouched, so the
 *      next launch starts from the region the user actually measured fastest.
 *      (Before this, the failover was written to storage and — because
 *      `initRegionPicker()` deliberately does not block boot on the probe —
 *      every subsequent launch connected to the fallback before the probe
 *      could correct it, one whole session at a time.)
 *   2. The probe only rewrites the stored preference when the winner beats the
 *      stored region by a REAL margin (see PICK_MARGIN_*). EU<->US is ~97 ms of
 *      tunnel, so a margin cannot mask a genuine continental difference, but it
 *      stops a noisy 8 ms probe from rewriting a preference.
 */
import { setPhoenixSocketOverride } from '../utils/phoenixSocketUrl';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1';
const STORAGE_KEY = 'apiboo.wsRegion';

// A probe winner must beat the stored region by BOTH of these to overwrite the
// stored preference. Absolute floor kills sub-noise churn on a fast link; the
// ratio keeps it meaningful on a slow one.
const PICK_MARGIN_MS = 25;
const PICK_MARGIN_RATIO = 0.15;

// After a session-scoped failover, re-probe once so the socket can come home on
// its next reconnect instead of at the next launch.
const FAILOVER_REPROBE_MS = 60_000;

let regions = [];
let currentId = null;
let reprobeTimer = null;

/** Session only: move the live socket endpoint, leave the stored preference alone. */
function applySession(region) {
  currentId = region.id;
  setPhoenixSocketOverride(region.url);
}

/** Session + storage: this is a statement about which region is genuinely best. */
function applyChoice(region) {
  applySession(region);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: region.id, url: region.url, ts: Date.now() }));
  } catch {}
}

function readStoredChoice() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

async function fetchJson(url, timeoutMs) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: ctrl.signal, cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

async function probeOnce(apiBase) {
  const t0 = performance.now();
  await fetchJson(`${apiBase}/regions`, 2500);
  return performance.now() - t0;
}

/**
 * @param {{ persist?: boolean }} [opts] — `persist: false` applies the pick to
 *   this session only. Used by the post-failover re-probe, which must be able
 *   to bring the socket home without touching a preference it never set.
 */
export async function probeAndPick(opts) {
  const persist = opts?.persist !== false;
  try {
    const data = await fetchJson(`${API_BASE}/regions`, 4000);
    regions = data?.regions || [];
    if (!regions.length) return;

    // Sequential, NOT Promise.all: two in-flight TLS handshakes on one uplink
    // contend with each other, and the winner is then partly a measure of the
    // contention rather than of the path.
    const results = [];
    for (const r of regions) {
      try {
        // Two rounds, keep the best: round one pays TLS setup, round two is
        // the honest RTT on the warm connection.
        const a = await probeOnce(r.api);
        const b = await probeOnce(r.api);
        results.push({ r, ms: Math.min(a, b) });
      } catch {
        results.push({ r, ms: Infinity });
      }
    }

    results.sort((a, b) => a.ms - b.ms);
    const winner = results[0];
    if (!winner || winner.ms === Infinity) return;

    const stored = readStoredChoice();
    const incumbent = stored?.id ? results.find((x) => x.r.id === stored.id) : null;

    let chosen = winner.r;
    if (incumbent && incumbent.ms !== Infinity && incumbent.r.id !== winner.r.id) {
      const gain = incumbent.ms - winner.ms;
      const beatsMargin = gain >= PICK_MARGIN_MS && gain >= incumbent.ms * PICK_MARGIN_RATIO;
      if (!beatsMargin) chosen = incumbent.r;
    }

    if (!persist) {
      applySession(chosen);
      return;
    }
    // Only touch storage when the persisted preference actually changes.
    if (!stored || stored.id !== chosen.id || stored.url !== chosen.url) applyChoice(chosen);
    else applySession(chosen);
  } catch {
    // Regions endpoint unreachable — keep whatever endpoint we already have.
  }
}

/** Call once at app bootstrap, before any socket connects. */
export function initRegionPicker() {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (cached?.url) {
      currentId = cached.id;
      setPhoenixSocketOverride(cached.url);
    }
  } catch {}
  // Deliberately NOT awaited, here or in index.js's bootstrap(): the probe is
  // up to ~5 s per region and awaiting it would trade a wrong region for a
  // white window on every cold start.
  probeAndPick();
}

/**
 * Rotate to the next region after repeated socket failures (dead node, bad
 * route). Returns the new socket URL, or null after clearing back to the
 * default API-base endpoint when there is nothing to rotate to.
 *
 * SESSION-SCOPED on purpose — see the module docblock. The single-region branch
 * below does still clear storage, and should: there it is discarding an
 * endpoint the backend no longer advertises, not recording a preference.
 */
export function failoverRegion() {
  if (!regions.length || regions.length === 1) {
    currentId = null;
    setPhoenixSocketOverride(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    return null;
  }
  const idx = regions.findIndex((r) => r.id === currentId);
  const next = regions[(idx + 1) % regions.length];
  applySession(next);
  scheduleFailoverReprobe();
  return next.url;
}

/**
 * One pending re-probe at a time; a second failover restarts the clock rather
 * than stacking timers.
 */
function scheduleFailoverReprobe() {
  if (reprobeTimer) clearTimeout(reprobeTimer);
  reprobeTimer = setTimeout(() => {
    reprobeTimer = null;
    probeAndPick({ persist: false });
  }, FAILOVER_REPROBE_MS);
}

/** The region this SESSION is on — which after a failover is not the stored one. */
export function getCurrentRegionId() {
  return currentId;
}
